import React, {useEffect} from 'react';
import {StyleSheet} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {getStateFromPath, InitialState} from '@react-navigation/native';
import {
  createStackNavigator,
  StackNavigationOptions,
} from '@react-navigation/stack';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import AppNavigationContainer from './app/components/AppNavigationContainer';
import ErrorBoundary from './app/components/ErrorBoundary';
import ToastBottom from './app/components/ToastBottom';
import config from './app/config';
import {AppRoute} from './app/models/AppRoute';
import {RootStackParamList} from './app/models/navigationTypes';
import DrawerNavigator from './app/navigation/DrawerNavigator';
import {navStructure} from './app/navigation/linking';
import RootCheckoutFlowStackNavigator from './app/navigation/RootCheckoutFlowNavigator';
import RootKitchenSinkStackNavigator from './app/navigation/RootKitchenSinkStackNavigator';
import RootRewardsFlowStackNavigator from './app/navigation/RootRewardsFlowStackNavigator';
import RootStoreSelectionFlowStackNavigator from './app/navigation/RootStoreSelectionFlowStackNavigator';
import AppBuildInfoScreen from './app/screens/AppBuildInfoScreen';
import BeerBoardScreen from './app/screens/beerBoard/BeerBoardScreen';
import {isBeerBoardUrl} from './app/screens/beerBoard/utils';
import SummerCashGrabScreen from './app/screens/games/SummerCashGrabScreen';
import SplashScreen from './app/screens/SplashScreen';
import {useStores} from './app/stores/use-stores';
import {reactNativeElementsTheme} from './app/style/reactNativeElementsTheme';
import { initialiseOneSignal } from './app/utils/onesignal';

const RootStack = createStackNavigator<RootStackParamList>();

const App = observer(() => {
  const {globalStore, initialiseApp, shopSelectionStore} = useStores();

  const [initialState, setInitialState] = React.useState<
    InitialState | undefined
  >(undefined);

  useEffect(() => {
    async function initialise() {
      await initialiseOneSignal(config);
      await initialiseApp();
    }
    initialise(); // only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (globalStore.isAppInitialising || globalStore.isSwitchingStore) {
    return <SplashScreen />;
  }

  if (isBeerBoardUrl()) {
    return <BeerBoardScreen />;
  }

  const selectedStore = shopSelectionStore?.selectedStore;
  const isStoreSelected = selectedStore?.id;

  // if the app was open through a link and a store was not selected, we
  // need to delay the original navigation until the store is selected
  if (
    isStoreSelected &&
    !globalStore.isAppInitialising &&
    !globalStore.isSwitchingStore &&
    !globalStore.wasStoreSelectedOnAppStart
  ) {
    if (globalStore.delayedNavigationPath) {
      const delayedInitialState = getStateFromPath(
        globalStore.delayedNavigationPath,
        navStructure,
      );

      setInitialState(delayedInitialState);
    }
    globalStore.setWasStoreSelectedOnAppStart(true);
  }

  const options: StackNavigationOptions = {headerShown: false};

  return (
    <SafeAreaProvider style={styles.main} testID="appRoot">
      <ErrorBoundary>
        <ThemeProvider theme={reactNativeElementsTheme}>
          <AppNavigationContainer initialState={initialState}>
            <RootStack.Navigator>
              {isStoreSelected ||globalStore.isAccountRoute || globalStore.isInformationRoute ? (
                <RootStack.Screen
                  name={AppRoute.ROOT}
                  component={DrawerNavigator}
                  options={{...options, title: 'Back'}}
                />
              ) : (
                <RootStack.Screen
                  name={AppRoute.ROOT_STORE_SELECTION_FLOW}
                  component={RootStoreSelectionFlowStackNavigator}
                  options={options}
                />
              )}
              {config.FF_LOYALTY_ENABLED ? (
                <>
                  <RootStack.Screen
                    name={AppRoute.ROOT_REWARDS_FLOW}
                    component={RootRewardsFlowStackNavigator}
                    options={options}
                  />
                  <RootStack.Screen
                    name={AppRoute.ROOT_CHECKOUT_FLOW}
                    component={RootCheckoutFlowStackNavigator}
                    options={options}
                  />
                </>
              ) : null}
              {config.KITCHENSINK_ENABLED ? (
                <RootStack.Screen
                  name={AppRoute.ROOT_KITCHENSINK}
                  component={RootKitchenSinkStackNavigator}
                  options={options}
                />
              ) : null}
              <RootStack.Screen
                name={AppRoute.ROOT_APP_BUILD_INFO}
                component={AppBuildInfoScreen}
                options={options}
              />
              <RootStack.Group screenOptions={{presentation: 'modal'}}>
                <RootStack.Screen
                  name={AppRoute.SUMMER_CASH_GRAB_GAME}
                  component={SummerCashGrabScreen}
                  options={{
                    title: 'Summer Cash Grab Game',
                  }}
                />
              </RootStack.Group>
            </RootStack.Navigator>
            <ToastBottom />
          </AppNavigationContainer>
        </ThemeProvider>
      </ErrorBoundary>
    </SafeAreaProvider>
  );
});

export default App;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    height: '100%',
  },
});
